import React, { useEffect, useRef } from 'react';
import axios from 'axios';

const LiveBusMap = ({ routeId, stop, routeColor = '#EAB308' }) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef({}); // { vehicleId: Marker }
    const animationStateRef = useRef({}); // { vehicleId: { startLat, startLng, endLat, endLng, startTime } }
    const rafIdRef = useRef(null);

    // Base API URL - Match the pattern used in App.js/Map.js
    const hostname = window.location.hostname;
    const API_BASE_URL = (hostname === 'localhost' || hostname === '127.0.0.1')
        ? '/api'
        : 'https://buzzbus-production.up.railway.app/api';

    const POLL_INTERVAL = 3000; // 3 seconds matching the server cache/fetch rate

    const createMarkerIcon = (color, heading) => {
        return {
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 8,
            fillColor: color,
            fillOpacity: 1,
            strokeWeight: 2.5,
            strokeColor: '#111827',
            rotation: heading,
        };
    };

    const updateMarkerIcon = (marker, color, heading) => {
        const icon = createMarkerIcon(color, heading);
        marker.setIcon(icon);
    };

    const updateVehicles = (vehicles) => {
        if (!mapInstanceRef.current) return;
        const currentIds = new Set();
        const now = performance.now();

        vehicles.forEach(v => {
            const vId = v.id;
            currentIds.add(vId);
            const targetLat = Number(v.latitude);
            const targetLng = Number(v.longitude);

            if (markersRef.current[vId]) {
                // Existing marker: Setup animation
                const marker = markersRef.current[vId];
                const currentPos = marker.getPosition();

                // Only animate if position changed
                if (Math.abs(currentPos.lat() - targetLat) > 0.000001 || Math.abs(currentPos.lng() - targetLng) > 0.000001) {
                    animationStateRef.current[vId] = {
                        startLat: currentPos.lat(),
                        startLng: currentPos.lng(),
                        endLat: targetLat,
                        endLng: targetLng,
                        startTime: now,
                        heading: Number(v.heading || 0)
                    };
                    updateMarkerIcon(marker, routeColor, Number(v.heading || 0));
                }
            } else {
                // New marker: Create immediately at target
                const icon = createMarkerIcon(routeColor, Number(v.heading || 0));
                const marker = new window.google.maps.Marker({
                    position: { lat: targetLat, lng: targetLng },
                    map: mapInstanceRef.current,
                    title: `Bus ${vId}`,
                    icon: icon,
                    zIndex: 999
                });
                markersRef.current[vId] = marker;
            }
        });

        // Cleanup removed vehicles
        Object.keys(markersRef.current).forEach(vId => {
            if (!currentIds.has(Number(vId)) && !currentIds.has(String(vId))) {
                markersRef.current[vId].setMap(null);
                delete markersRef.current[vId];
                delete animationStateRef.current[vId];
            }
        });
    };

    const animate = () => {
        rafIdRef.current = requestAnimationFrame(animate);
        const now = performance.now();

        Object.keys(animationStateRef.current).forEach(vId => {
            const state = animationStateRef.current[vId];
            const marker = markersRef.current[vId];
            if (!state || !marker) return;

            const elapsed = now - state.startTime;
            const progress = Math.min(elapsed / POLL_INTERVAL, 1.0);

            const lat = state.startLat + (state.endLat - state.startLat) * progress;
            const lng = state.startLng + (state.endLng - state.startLng) * progress;

            marker.setPosition({ lat, lng });

            if (progress >= 1.0) {
                delete animationStateRef.current[vId];
            }
        });
    };

    useEffect(() => {
        // 1. Initialize Map
        if (!mapRef.current || !window.google) return;

        if (!mapInstanceRef.current) {
            mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                center: { lat: stop.latitude, lng: stop.longitude },
                zoom: 17,
                disableDefaultUI: true,
                zoomControl: true,
                styles: [
                    {
                        featureType: "poi",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }]
                    }
                ]
            });

            // Add Stop Marker (Red circle with white square inside - stop symbol)
            new window.google.maps.Marker({
                position: { lat: stop.latitude, lng: stop.longitude },
                map: mapInstanceRef.current,
                title: stop.name,
                icon: {
                    url: 'data:image/svg+xml,' + encodeURIComponent(`
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" fill="#EF4444" stroke="#FFFFFF" stroke-width="2"/>
                            <rect x="6" y="9" width="12" height="6" fill="#FFFFFF" rx="1"/>
                        </svg>
                    `),
                    scaledSize: new window.google.maps.Size(28, 28),
                    anchor: new window.google.maps.Point(14, 14)
                }
            });
        } else {
            // Map already exists, recenter on new stop
            mapInstanceRef.current.setCenter({ lat: stop.latitude, lng: stop.longitude });
            mapInstanceRef.current.setZoom(17);
        }

        // 2. Poll for Vehicles
        const fetchVehicles = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/RouteSearch/vehicles/${routeId}`);
                const vehicles = response.data || [];
                updateVehicles(vehicles);
            } catch (error) {
                console.error("Error fetching vehicles:", error);
            }
        };

        // Initial fetch
        fetchVehicles();
        const intervalId = setInterval(fetchVehicles, POLL_INTERVAL);

        // Start Animation Loop
        animate();

        return () => {
            clearInterval(intervalId);
            if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeId, stop, API_BASE_URL]);

    return (
        <div
            ref={mapRef}
            className="timeline-map-frame"
            style={{ width: '100%', height: '400px', borderRadius: '0.25rem' }}
        />
    );
};

export default LiveBusMap;
